import {Button, Stack, Typography} from '@mui/material';
import React, {useState} from 'react';

import {BillData} from 'domain/usecases';
import {BillsTaggingEnum} from 'main/analytics/gaTagsEnum';
import {AlertInfo, IconIllustration, Modal} from 'presentation/components';
import {Snackbar} from 'presentation/components/Snackbar';
import {useHostName} from 'presentation/hooks';
import Icon from 'presentation/icons';
import {translations} from 'presentation/translations-new';
import {formatDateString, formatText} from 'presentation/utils';
import {OlderBillsModalProps} from './types';
import {OpenBillsTestIdEnum} from 'presentation/constants';

const {copiedCodeMessage} = translations['pt-br'].privateFlow.myFinancial;
const {olderBillsModal, olderInvoicesModal} = translations['pt-br'].privateFlow.myFinancial.openBills;

export function OlderBillsModal({
	isOpen,
	onClose,
	numberOfBills,
	mostOldBill,
	currentBill,
	onGoToBillDetails,
	onDownload,
	actionType,
}: OlderBillsModalProps) {
	const {isNotredameSaude} = useHostName();
	const isSingular = numberOfBills === 1;
	const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);

	const formatButtonText = (template: string, bill: BillData | null) => {
		if (!bill?.dtPrevista) return template.replace('{month}', '');
		const month = formatText(formatDateString(bill.dtPrevista, 'dd/MM/yyyy', 'MMMM'));
		return template.replace('{month}', month);
	};

	const getCurrentBillButtonText = () => {
		if (onDownload) {
			return formatButtonText(
				isNotredameSaude ? olderInvoicesModal.buttonDownload : olderBillsModal.buttonDownload,
				currentBill,
			);
		}
		if (actionType === 'copy') {
			return formatButtonText(olderBillsModal.buttonCopyBarcode, currentBill);
		}
		return formatButtonText(olderBillsModal.buttonContinue, currentBill);
	};

	const handleCurrentBillClick = () => {
		if (onDownload) {
			onClose();
			onDownload();
			return;
		}

		if (actionType === 'copy' && currentBill?.linhaDigitavel?.cdBarra) {
			onClose();
			navigator.clipboard.writeText(currentBill.linhaDigitavel.cdBarra);
			setIsSnackbarOpen(true);
			return;
		}

		onGoToBillDetails(currentBill!);
	};

	return (
		<>
			<Modal.Root open={isOpen} onClose={onClose}>
				<Modal.Header
					modalDataTagId={BillsTaggingEnum.VISUALIZOU_MODAL_BOLETO_ANTIGO_PENDENTE}
					closeBtnDataTagId={BillsTaggingEnum.CLICK_FECHAR_MODAL_BOLETO_ANTIGO_PENDENTE}
					title={olderBillsModal.title}
					onClose={onClose}
				/>
				<Modal.Content>
					<Stack gap={2} alignItems="center">
						<IconIllustration customIconName="info-filled" variant="info" />
						<Typography variant="h6">
							{isSingular
								? isNotredameSaude
									? olderInvoicesModal.headerTitle.singular
									: olderBillsModal.headerTitle.singular
								: isNotredameSaude
									? olderInvoicesModal.headerTitle.plural
									: olderBillsModal.headerTitle.plural}
						</Typography>
						<Typography variant="body1" textAlign="center">
							{isSingular
								? isNotredameSaude
									? olderInvoicesModal.description.singular
									: olderBillsModal.description.singular
								: isNotredameSaude
									? olderInvoicesModal.description.plural
									: olderBillsModal.description.plural}
						</Typography>
						<AlertInfo.Root severity="warning" icon={<Icon name="warning-outlined" />}>
							<AlertInfo.Description>
								<Typography variant="body2">
									{olderBillsModal.alertMessage}
								</Typography>
							</AlertInfo.Description>
						</AlertInfo.Root>
					</Stack>
				</Modal.Content>

				<Modal.Footer>
					<Stack width="100%" gap={1}>
						{mostOldBill && (
							<Button
								variant="contained"
								fullWidth
								data-tag-id={
									BillsTaggingEnum.CLICK_IR_PARA_BOLETO_MAIS_ANTIGO_PENDENTE
								}
								data-testid={
									OpenBillsTestIdEnum.BTN_MODAL_IR_PARA_BOLETO_MAIS_ANTIGO
								}
								onClick={() => onGoToBillDetails(mostOldBill)}>
								{formatButtonText(
									isNotredameSaude
										? olderBillsModal.buttonGoToOldestNotredameSaude
										: olderBillsModal.buttonGoToOldest,
									mostOldBill,
								)}
							</Button>
						)}
						{currentBill && (
							<Button
								variant="outlined"
								fullWidth
								onClick={handleCurrentBillClick}
								data-tag-id={
									BillsTaggingEnum.CLICK_CONTINUAR_DETALHES_DO_BOLETO_MAIS_ATUAL
								}
								data-testid={OpenBillsTestIdEnum.BTN_MODAL_ACAO_BOLETO_ATUAL}>
								{getCurrentBillButtonText()}
							</Button>
						)}
					</Stack>
				</Modal.Footer>
			</Modal.Root>
			<Snackbar
				onClose={() => setIsSnackbarOpen(false)}
				message={copiedCodeMessage}
				open={isSnackbarOpen}
			/>
		</>
	);
}
